<div class="modal-header">
  <h4 class="modal-title">
    @if (addOccurrenceMode) {
      Add Occurrence
    } @else if (isTemplateMode) {
      Edit Series
    } @else if (isChildMode) {
      Edit Occurrence
    } @else {
      {{ isNew ? "Create Event" : "Edit Event" }}
    }
  </h4>
  <button class="close" aria-label="Close" (click)="activeModal.dismiss()">
    <span aria-hidden="true">&times;</span>
  </button>
</div>
<div class="modal-body" ngbAutofocus>
  @if (addOccurrenceMode) {
    <p class="text-muted m-b-20">Adding a new date to "{{ title }}"</p>
  }

  @if (isTemplateMode) {
    <div class="alert alert-info m-b-20">Changes here will apply to all future occurrences in this series.</div>
  }

  @if (showSharedFields) {
    <!-- Title -->
    <div class="form-group">
      <label class="control-label">
        Title
        <span class="text-danger">*</span>
      </label>
      <input type="text" class="form-control" [(ngModel)]="title" (ngModelChange)="onTitleChange()" placeholder="Event title" required />
    </div>

    <!-- Slug -->
    <div class="form-group m-t-10">
      <label class="control-label">
        Slug
        <span class="text-danger">*</span>
      </label>
      <small class="text-muted d-block">Used for friendly URLs like /events/{{ slug || "my-event" }}</small>
      <input type="text" class="form-control" [(ngModel)]="slug" (input)="onSlugInput()" placeholder="auto-generated-from-title" required />
    </div>
  }

  <!-- Description -->
  <div class="form-group m-t-10">
    <label class="control-label">
      Description
      <span class="text-danger">*</span>
    </label>
    <fui-quill-editor [content]="description" (contentChange)="description = $event" placeholder="Event description..."></fui-quill-editor>

    <!-- Localized descriptions (mirrors the PDP editor). Auto-translate fills the other languages
         (OpenAI, reads the saved English copy — save first); each is editable behind the toggle.
         Missing languages fall back to English on the embed. -->
    @if (event?.id) {
      <div class="d-flex align-items-center flex-wrap m-t-5">
        <button type="button" class="btn btn-sm btn-secondary m-r-10" [disabled]="translating" (click)="autoTranslateDescription()">
          {{ translating ? "Translating…" : "Auto-translate" }}
        </button>
        <button type="button" class="btn btn-sm btn-blank" (click)="showTranslations = !showTranslations">
          {{ showTranslations ? "Hide translations" : "Show translations (" + otherLanguages.length + ")" }}
        </button>
        <small class="text-muted m-l-10">Save your English edits first, then Auto-translate. Missing languages fall back to English.</small>
      </div>

      @if (showTranslations) {
        <div class="m-t-10">
          @for (lang of otherLanguages; track lang.code) {
            <div class="form-group m-b-15">
              <label class="control-label d-flex align-items-center">
                @if (lang.flag_icon) {
                  <img [src]="lang.flag_icon" class="m-r-5" style="width: 20px; height: auto" />
                }
                {{ lang.translated_name || lang.name }}
                <span class="text-muted m-l-5">({{ lang.code }})</span>
                @if (!descriptionsByLang[lang.code]) {
                  <span class="text-muted m-l-5">— falls back to English</span>
                }
              </label>
              @if (quillReady) {
                <fui-quill-editor
                  [content]="descriptionsByLang[lang.code] || ''"
                  (contentChange)="descriptionsByLang[lang.code] = $event"
                  [placeholder]="'Description (' + lang.code + ')...'"
                ></fui-quill-editor>
              }
            </div>
          }
        </div>
      }
    }
  </div>

  @if (!isTemplateMode) {
    <!-- Date & Time -->
    <div class="d-flex m-t-10 flex-wrap">
      <div class="form-group flex-fill m-r-10">
        <label class="control-label">
          Start Date/Time
          <span class="text-danger">*</span>
        </label>
        <input type="datetime-local" class="form-control" [(ngModel)]="startDatetime" (ngModelChange)="onStartDateChange()" required />
      </div>
      <div class="form-group flex-fill m-r-10">
        <label class="control-label">
          End Date/Time
          <span class="text-danger">*</span>
        </label>
        <input type="datetime-local" class="form-control" [(ngModel)]="endDatetime" (ngModelChange)="onEndDateChange()" required />
      </div>
      <div class="form-group" style="width: 200px">
        <label class="control-label">Timezone</label>
        <select class="form-control" [(ngModel)]="timezone">
          @for (tz of timezones; track tz) {
            <option [value]="tz">{{ tz }}</option>
          }
        </select>
      </div>
    </div>

    @if (showSharedFields && isNew) {
      <!-- Recurring (new events only) -->
      <div class="m-t-10">
        <div class="form-check">
          <input class="form-check-input" type="checkbox" [(ngModel)]="isRecurring" id="eventRecurring" />
          <label class="form-check-label" for="eventRecurring">Recurring Event</label>
        </div>
        @if (isRecurring) {
          <div class="d-flex m-t-10 flex-wrap">
            <div class="form-group m-r-10" style="width: 150px">
              <label class="control-label">Frequency</label>
              <select class="form-control" [(ngModel)]="recurringFrequency" (ngModelChange)="onRecurringSettingChange()">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="monthly">Monthly</option>
                <option value="intermittent">Intermittent</option>
              </select>
            </div>
            @if (recurringFrequency !== "intermittent") {
              <div class="form-group" style="width: 200px">
                <label class="control-label">
                  End Date
                  <span class="text-danger">*</span>
                </label>
                <input type="date" class="form-control" [(ngModel)]="recurringEndDate" (ngModelChange)="onRecurringSettingChange()" required />
              </div>
            }
          </div>
          @if (exceedsOccurrenceCap) {
            <div class="alert alert-warning m-t-10">
              This schedule would create {{ pendingOccurrenceCount }} occurrences, but we create a maximum of {{ occurrenceCap }} at a time. The first {{ occurrenceCap }} will be created and the
              series will end on the last one — edit the series afterwards to extend it further.
            </div>
          }
          @if (recurringFrequency === "intermittent") {
            <small class="text-muted d-block m-t-5">The first event will be created using the date above. You can add more events after creating this recurring event.</small>
          }
          @if (recurringFrequency === "monthly" && startDatetime) {
            <div class="m-t-10">
              <small class="text-muted d-block m-b-5">Repeat on:</small>
              <div class="d-flex">
                <div class="form-check m-r-20">
                  <input class="form-check-input" type="radio" name="monthlyPattern" id="patternDate" value="date" [(ngModel)]="monthlyPattern" (ngModelChange)="onRecurringSettingChange()" />
                  <label class="form-check-label" for="patternDate">Same date each month</label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" name="monthlyPattern" id="patternWeekday" value="weekday" [(ngModel)]="monthlyPattern" (ngModelChange)="onRecurringSettingChange()" />
                  <label class="form-check-label" for="patternWeekday">{{ monthlyPatternLabel }} of each month</label>
                </div>
              </div>
            </div>
          }
        }
      </div>
    }
  }

  @if (isTemplateMode) {
    <!-- Timezone only for template -->
    <div class="form-group m-t-10" style="width: 200px">
      <label class="control-label">Timezone</label>
      <select class="form-control" [(ngModel)]="timezone">
        @for (tz of timezones; track tz) {
          <option [value]="tz">{{ tz }}</option>
        }
      </select>
    </div>
  }

  <!-- Recurring schedule of an existing series. Editable so a series can be extended: pushing the
       end date out (or changing the frequency) creates the occurrences the new schedule calls for,
       instead of the admin adding a year of dates one at a time. -->
  @if (editingRecurrence) {
    <div class="m-t-20 p-15 trans-bg border-radius10">
      <label class="control-label d-block">Recurring Schedule</label>
      <small class="text-muted d-block m-b-10">
        {{ recurringInstanceCount || "No" }} occurrence{{ recurringInstanceCount === 1 ? "" : "s" }} created so far. Extend the end date to schedule more.
      </small>
      <div class="d-flex flex-wrap">
        <div class="form-group m-r-10" style="width: 150px">
          <label class="control-label">Frequency</label>
          <select class="form-control" [(ngModel)]="recurringFrequency" (ngModelChange)="onRecurringSettingChange()">
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Biweekly</option>
            <option value="monthly">Monthly</option>
            <option value="intermittent">Intermittent</option>
          </select>
        </div>
        @if (recurringFrequency !== "intermittent") {
          <div class="form-group" style="width: 200px">
            <label class="control-label">
              End Date
              <span class="text-danger">*</span>
            </label>
            <input type="date" class="form-control" [(ngModel)]="recurringEndDate" (ngModelChange)="onRecurringSettingChange()" required />
          </div>
        }
      </div>
      @if (recurringFrequency === "intermittent") {
        <small class="text-muted d-block">Intermittent series have no schedule — add each date yourself with "Add occurrence".</small>
      }
      @if (recurringFrequency === "monthly" && startDatetime) {
        <div class="m-t-10">
          <small class="text-muted d-block m-b-5">Repeat on:</small>
          <div class="d-flex">
            <div class="form-check m-r-20">
              <input class="form-check-input" type="radio" name="editMonthlyPattern" id="editPatternDate" value="date" [(ngModel)]="monthlyPattern" (ngModelChange)="onRecurringSettingChange()" />
              <label class="form-check-label" for="editPatternDate">Same date each month</label>
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                name="editMonthlyPattern"
                id="editPatternWeekday"
                value="weekday"
                [(ngModel)]="monthlyPattern"
                (ngModelChange)="onRecurringSettingChange()"
              />
              <label class="form-check-label" for="editPatternWeekday">{{ monthlyPatternLabel }} of each month</label>
            </div>
          </div>
        </div>
      }
      @if (scheduleDirty) {
        <div class="alert alert-warning m-t-10 m-b-0">
          @if (exceedsOccurrenceCap) {
            This schedule needs {{ pendingOccurrenceCount }} more occurrences, but we create a maximum of {{ occurrenceCap }} at a time. Saving creates the next {{ occurrenceCap }} and ends the series
            on the last one created — extend the end date again afterwards to add the rest.
          } @else {
            Saving will create {{ pendingOccurrenceCount }} {{ pendingOccurrenceCount === 1 ? "occurrence" : "occurrences" }} between now and the end date. Existing occurrences are left as they are.
          }
        </div>
      }
    </div>
  }

  <!-- Image Upload -->
  <div class="form-group m-t-10">
    <label class="control-label">Event Image</label>
    @if (!imageUrl) {
      <div>
        <input type="file" accept="image/*" (change)="onFileSelected($event)" [disabled]="uploading" />
        @if (uploading) {
          <small class="text-muted m-l-10">Uploading...</small>
        }
      </div>
    }
    @if (imageUrl) {
      <div class="d-flex align-items-center">
        <img [src]="imageUrl" style="width: 80px; height: 80px; object-fit: cover; border-radius: 4px" class="m-r-10" />
        <button class="btn btn-sm btn-secondary" (click)="removeImage()">Remove</button>
      </div>
    }
  </div>

  <!-- Livestream platform picker. Vimeo and YouTube are peers; picking "None" hides the ID
       inputs and clears IDs on save. -->
  @if (!isRecurring && !isTemplateMode) {
    <div class="form-group m-t-10">
      <label class="control-label">Livestream Platform</label>
      <select class="form-control" [(ngModel)]="livestreamPlatform">
        <option value="">None (not a livestream)</option>
        <option value="vimeo">Vimeo</option>
        <option value="youtube">YouTube</option>
      </select>
    </div>
    @if (livestreamPlatform === "vimeo") {
      <div class="d-flex m-t-10">
        <div class="form-group flex-fill m-r-10">
          <label class="control-label">Vimeo Livestream ID</label>
          <input type="text" class="form-control" [(ngModel)]="vimeoLivestreamId" placeholder="e.g. 4123456" />
          <span class="tiny-link" (click)="findVimeoIdsInstructions(true)">How do I find this?</span>
        </div>
        <div class="form-group flex-fill">
          <label class="control-label">Vimeo Livestream Clip ID</label>
          <input type="text" class="form-control" [(ngModel)]="vimeoVideoId" placeholder="e.g. 999999999" />
          <span class="tiny-link" (click)="findVimeoIdsInstructions()">How do I find this?</span>
        </div>
      </div>
      @if ((vimeoLivestreamId && !vimeoVideoId) || (!vimeoLivestreamId && vimeoVideoId)) {
        <small class="text-danger">Both Vimeo Livestream ID and Clip ID are required if either is provided.</small>
      }
    }
    @if (livestreamPlatform === "youtube") {
      <div class="form-group m-t-10">
        <label class="control-label">YouTube Video ID</label>
        <input type="text" class="form-control" [(ngModel)]="youtubeVideoId" placeholder="e.g. dQw4w9WgXcQ" />
        <small class="text-muted d-block m-t-5">
          From the watch URL (youtube.com/watch?v=
          <strong>VIDEO_ID</strong>
          ) or the "Share" panel on your live broadcast in YouTube Studio. We'll auto-fetch the channel and live-chat IDs on save.
        </small>
      </div>
    }
    @if (livestreamPlatform) {
      <div class="form-group m-t-10">
        <label class="control-label">
          Trailer YouTube ID
          <span class="text-muted" style="font-weight: 400">(optional)</span>
        </label>
        <input type="text" class="form-control" [(ngModel)]="previewVideoId" placeholder="e.g. dQw4w9WgXcQ" />
        <small class="text-muted d-block m-t-5">Shows a "Watch The Trailer" button on the countdown that plays this YouTube video in a modal.</small>
      </div>
    }
  }

  <!-- Per-occurrence comments toggle (child edit / add occurrence). The shared/template
       value lives in the Location section below; this overrides it for this one event. -->
  @if (isChildMode || addOccurrenceMode) {
    <myp-switch class="d-block m-t-15" [active]="commentsEnabled" [label]="'Show comments on this livestream'" [labelToRight]="true" (toggle)="commentsEnabled = !commentsEnabled"></myp-switch>
    <myp-switch class="d-block m-t-10" [active]="shopEnabled" [label]="'Enable shopping rail on this livestream'" [labelToRight]="true" (toggle)="shopEnabled = !shopEnabled"></myp-switch>
    <myp-switch
      class="d-block m-t-10"
      [active]="signupEnabled"
      [label]="'Enable sign-up / email capture on this livestream'"
      [labelToRight]="true"
      (toggle)="signupEnabled = !signupEnabled"
    ></myp-switch>
    @if (signupEnabled) {
      <div class="m-t-10">
        <p class="text-muted m-b-10" style="font-size: 12px">
          The sign-up form can behave differently before the stream (countdown) and while it's live (livestream). Pick a state to edit its button text, messages, Iterable list, and source tag.
        </p>
        <myp-segmented-bar
          class="d-block m-b-10"
          [white]="true"
          (selected)="switchSignupState($event)"
          [options]="[
            { name: 'Countdown', id: 'countdown', selected: signupState === 'countdown' },
            { name: 'Livestream', id: 'livestream', selected: signupState === 'livestream' },
          ]"
        ></myp-segmented-bar>
        <div class="signup-config-fields">
          <div class="d-flex flex-wrap signup-config-row">
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupOccHeadline-' + signupState">Headline</label>
              <input class="form-control" type="text" [(ngModel)]="signupConfig[signupState].headline" [id]="'signupOccHeadline-' + signupState" placeholder="e.g. Stay In The Know!" />
            </div>
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupOccButton-' + signupState">
                Button text
                <span class="text-muted" style="font-weight: 400">(opens the form)</span>
              </label>
              <input class="form-control" type="text" [(ngModel)]="signupConfig[signupState].button_text" [id]="'signupOccButton-' + signupState" placeholder="Sign Up (e.g. Enter To Win!)" />
            </div>
          </div>
          <div class="d-flex flex-wrap signup-config-row">
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupOccSubmitButton-' + signupState">
                Submit button text
                <span class="text-muted" style="font-weight: 400">(submits the form)</span>
              </label>
              <input
                class="form-control"
                type="text"
                [(ngModel)]="signupConfig[signupState].submit_button_text"
                [id]="'signupOccSubmitButton-' + signupState"
                placeholder="Sign Up (e.g. Enter To Win!)"
              />
            </div>
          </div>
          <div class="d-flex flex-wrap signup-config-row">
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupOccMessage-' + signupState">Sign-up form message</label>
              <textarea
                class="form-control"
                rows="2"
                [(ngModel)]="signupConfig[signupState].form_message"
                [id]="'signupOccMessage-' + signupState"
                placeholder="e.g. Enter your email for a chance to win an all-new Player II"
              ></textarea>
            </div>
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupOccOptin-' + signupState">Opt-in checkbox message</label>
              <textarea
                class="form-control"
                rows="2"
                [(ngModel)]="signupConfig[signupState].optin_message"
                [id]="'signupOccOptin-' + signupState"
                placeholder="e.g. Yes, send me Fender news, offers, and updates by email."
              ></textarea>
            </div>
          </div>
          <div class="m-b-5">
            <myp-switch
              class="d-block m-b-5"
              [active]="signupConfig[signupState].optin_required"
              [label]="'Require marketing opt-in to submit'"
              [labelToRight]="true"
              (toggle)="signupConfig[signupState].optin_required = !signupConfig[signupState].optin_required"
            ></myp-switch>
            @if (signupConfig[signupState].optin_required) {
              <div class="form-group m-b-5">
                <label class="control-label" [for]="'signupOccOptinRequiredMsg-' + signupState">Opt-in required message</label>
                <textarea
                  class="form-control"
                  rows="2"
                  [(ngModel)]="signupConfig[signupState].optin_required_message"
                  [id]="'signupOccOptinRequiredMsg-' + signupState"
                  placeholder="e.g. Please check the box to sign up."
                ></textarea>
              </div>
            }
          </div>
          <div class="d-flex flex-wrap signup-config-row">
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupOccSuccess-' + signupState">Success message</label>
              <textarea
                class="form-control"
                rows="2"
                [(ngModel)]="signupConfig[signupState].success_message"
                [id]="'signupOccSuccess-' + signupState"
                placeholder="e.g. Thanks for signing up — we'll let you know."
              ></textarea>
            </div>
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupOccSuccessHeader-' + signupState">Success header</label>
              <textarea class="form-control" rows="2" [(ngModel)]="signupConfig[signupState].success_header" [id]="'signupOccSuccessHeader-' + signupState" placeholder="e.g. You're in!"></textarea>
            </div>
          </div>
          <div class="form-group m-b-5">
            <label class="control-label" [for]="'signupOccLegal-' + signupState">
              Legal disclaimer
              <span class="text-muted" style="font-weight: 400">(HTML allowed; leave blank for the default)</span>
            </label>
            <textarea
              class="form-control"
              rows="2"
              [(ngModel)]="signupConfig[signupState].legal_disclaimer"
              [id]="'signupOccLegal-' + signupState"
              placeholder='e.g. By signing up you agree to our &lt;a href="..."&gt;Terms&lt;/a&gt;.'
            ></textarea>
          </div>
          <div class="d-flex flex-wrap signup-config-row">
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupOccList-' + signupState">Iterable list ID</label>
              <input class="form-control" type="text" [(ngModel)]="signupConfig[signupState].list_id" [id]="'signupOccList-' + signupState" placeholder="e.g. 123456" />
            </div>
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupOccSource-' + signupState">Source</label>
              <input class="form-control" type="text" [(ngModel)]="signupConfig[signupState].source" [id]="'signupOccSource-' + signupState" placeholder="e.g. player-ii-countdown" />
            </div>
          </div>
        </div>
      </div>
    }
  }

  @if (showSharedFields) {
    <!-- Location -->
    <h5 class="m-t-20 m-b-10">Location</h5>
    <!-- Stacked feature toggles. myp-switch emits (toggle) without echoing the new value, so flip
         the bound property directly. -->
    <div class="m-b-10">
      <myp-switch class="d-block m-b-10" [active]="online" [label]="'Online Event'" [labelToRight]="true" (toggle)="online = !online"></myp-switch>
      <myp-switch class="d-block m-b-10" [active]="fspExclusive" [label]="'Fender Studio Pro+ Exclusive'" [labelToRight]="true" (toggle)="fspExclusive = !fspExclusive"></myp-switch>
      <myp-switch class="d-block m-b-10" [active]="commentsEnabled" [label]="'Show comments on livestream'" [labelToRight]="true" (toggle)="commentsEnabled = !commentsEnabled"></myp-switch>
      <myp-switch class="d-block m-b-10" [active]="shopEnabled" [label]="'Enable shopping rail'" [labelToRight]="true" (toggle)="shopEnabled = !shopEnabled"></myp-switch>
      <myp-switch class="d-block m-b-10" [active]="signupEnabled" [label]="'Enable sign-up / email capture'" [labelToRight]="true" (toggle)="signupEnabled = !signupEnabled"></myp-switch>
      <myp-switch class="d-block" [active]="hideFromPortal" [label]="'Hide from MyFender (fender.com embed only)'" [labelToRight]="true" (toggle)="hideFromPortal = !hideFromPortal"></myp-switch>
      @if (hideFromPortal) {
        <p class="text-muted m-t-5 m-b-0" style="font-size: 12px">This event won't appear in the MyFender calendar, listings, or app — only in the fender.com embed.</p>
      }
    </div>
    @if (signupEnabled) {
      <p class="text-muted m-b-10" style="font-size: 12px">
        The sign-up form can behave differently before the stream (countdown) and while it's live (livestream). Pick a state to edit its button text, messages, Iterable list, and source tag.
      </p>
      <div class="m-b-10">
        <myp-segmented-bar
          class="d-block m-b-10"
          [white]="true"
          (selected)="switchSignupState($event)"
          [options]="[
            { name: 'Countdown', id: 'countdown', selected: signupState === 'countdown' },
            { name: 'Livestream', id: 'livestream', selected: signupState === 'livestream' },
          ]"
        ></myp-segmented-bar>
        <div class="signup-config-fields">
          <div class="d-flex flex-wrap signup-config-row">
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupHeadline-' + signupState">Headline</label>
              <input class="form-control" type="text" [(ngModel)]="signupConfig[signupState].headline" [id]="'signupHeadline-' + signupState" placeholder="e.g. Stay In The Know!" />
            </div>
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupButton-' + signupState">
                Button text
                <span class="text-muted" style="font-weight: 400">(opens the form)</span>
              </label>
              <input class="form-control" type="text" [(ngModel)]="signupConfig[signupState].button_text" [id]="'signupButton-' + signupState" placeholder="Sign Up (e.g. Enter To Win!)" />
            </div>
          </div>
          <div class="d-flex flex-wrap signup-config-row">
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupSubmitButton-' + signupState">
                Submit button text
                <span class="text-muted" style="font-weight: 400">(submits the form)</span>
              </label>
              <input
                class="form-control"
                type="text"
                [(ngModel)]="signupConfig[signupState].submit_button_text"
                [id]="'signupSubmitButton-' + signupState"
                placeholder="Sign Up (e.g. Enter To Win!)"
              />
            </div>
          </div>
          <div class="d-flex flex-wrap signup-config-row">
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupMessage-' + signupState">Sign-up form message</label>
              <textarea
                class="form-control"
                rows="2"
                [(ngModel)]="signupConfig[signupState].form_message"
                [id]="'signupMessage-' + signupState"
                placeholder="e.g. Enter your email for a chance to win an all-new Player II"
              ></textarea>
            </div>
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupOptin-' + signupState">Opt-in checkbox message</label>
              <textarea
                class="form-control"
                rows="2"
                [(ngModel)]="signupConfig[signupState].optin_message"
                [id]="'signupOptin-' + signupState"
                placeholder="e.g. Yes, send me Fender news, offers, and updates by email."
              ></textarea>
            </div>
          </div>
          <div class="m-b-5">
            <myp-switch
              class="d-block m-b-5"
              [active]="signupConfig[signupState].optin_required"
              [label]="'Require marketing opt-in to submit'"
              [labelToRight]="true"
              (toggle)="signupConfig[signupState].optin_required = !signupConfig[signupState].optin_required"
            ></myp-switch>
            @if (signupConfig[signupState].optin_required) {
              <div class="form-group m-b-5">
                <label class="control-label" [for]="'signupOptinRequiredMsg-' + signupState">Opt-in required message</label>
                <textarea
                  class="form-control"
                  rows="2"
                  [(ngModel)]="signupConfig[signupState].optin_required_message"
                  [id]="'signupOptinRequiredMsg-' + signupState"
                  placeholder="e.g. Please check the box to sign up."
                ></textarea>
              </div>
            }
          </div>
          <div class="d-flex flex-wrap signup-config-row">
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupSuccess-' + signupState">Success message</label>
              <textarea
                class="form-control"
                rows="2"
                [(ngModel)]="signupConfig[signupState].success_message"
                [id]="'signupSuccess-' + signupState"
                placeholder="e.g. Thanks for signing up — we'll let you know."
              ></textarea>
            </div>
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupSuccessHeader-' + signupState">Success header</label>
              <textarea class="form-control" rows="2" [(ngModel)]="signupConfig[signupState].success_header" [id]="'signupSuccessHeader-' + signupState" placeholder="e.g. You're in!"></textarea>
            </div>
          </div>
          <div class="form-group m-b-5">
            <label class="control-label" [for]="'signupLegal-' + signupState">
              Legal disclaimer
              <span class="text-muted" style="font-weight: 400">(HTML allowed; leave blank for the default)</span>
            </label>
            <textarea
              class="form-control"
              rows="2"
              [(ngModel)]="signupConfig[signupState].legal_disclaimer"
              [id]="'signupLegal-' + signupState"
              placeholder='e.g. By signing up you agree to our &lt;a href="..."&gt;Terms&lt;/a&gt;.'
            ></textarea>
          </div>
          <div class="d-flex flex-wrap signup-config-row">
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupList-' + signupState">Iterable list ID</label>
              <input class="form-control" type="text" [(ngModel)]="signupConfig[signupState].list_id" [id]="'signupList-' + signupState" placeholder="e.g. 123456" />
            </div>
            <div class="form-group m-b-5">
              <label class="control-label" [for]="'signupSource-' + signupState">Source</label>
              <input class="form-control" type="text" [(ngModel)]="signupConfig[signupState].source" [id]="'signupSource-' + signupState" placeholder="e.g. player-ii-countdown" />
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Embed destinations -->
    <h5 class="m-t-20 m-b-10">Embed Destinations</h5>
    <p class="text-muted m-b-10" style="font-size: 12px">Show this livestream's countdown and player in the embeddable iframe on these sites.</p>
    <div class="d-flex align-items-center m-b-10">
      @for (dest of knownDestinations; track dest.id) {
        <div class="form-check m-r-20">
          <input class="form-check-input" type="checkbox" [id]="'eventDest_' + dest.id" [checked]="destinations.includes(dest.id)" (change)="toggleDestination(dest.id)" />
          <label class="form-check-label" [for]="'eventDest_' + dest.id">{{ dest.label }}</label>
        </div>
      }
    </div>

    <!-- Embed code for each selected destination -->
    @for (dest of knownDestinations; track dest.id) {
      @if (destinations.includes(dest.id)) {
        <div class="embed-code-block m-b-10">
          <div class="d-flex justify-content-between align-items-center m-b-5">
            <label class="control-label m-b-0" style="font-size: 12px">{{ dest.label }} embed code</label>
            <button type="button" class="btn btn-sm btn-secondary d-flex align-items-center" (click)="copyEmbedCode(dest.id)">
              <fa-icon [icon]="copiedDestination === dest.id ? 'check' : 'copy'" class="m-r-5"></fa-icon>
              {{ copiedDestination === dest.id ? "Copied" : "Copy" }}
            </button>
          </div>
          <textarea class="form-control" readonly rows="3" style="font-family: monospace; font-size: 12px" (click)="$any($event.target).select()">{{ embedCode(dest.id) }}</textarea>
        </div>
      }
    }

    @if (!online) {
      <div class="form-group">
        <label class="control-label">Address</label>
        <input type="text" class="form-control" [(ngModel)]="address" />
      </div>
      <div class="d-flex m-t-10">
        <div class="form-group flex-fill m-r-10">
          <label class="control-label">City</label>
          <input type="text" class="form-control" [(ngModel)]="city" />
        </div>
        <div class="form-group flex-fill m-r-10">
          <label class="control-label">State</label>
          <input type="text" class="form-control" [(ngModel)]="state" />
        </div>
        <div class="form-group flex-fill m-r-10">
          <label class="control-label">Zip</label>
          <input type="text" class="form-control" [(ngModel)]="zip" />
        </div>
        <div class="form-group flex-fill">
          <label class="control-label">Country</label>
          <input type="text" class="form-control" [(ngModel)]="country" />
        </div>
      </div>
    }
  }

  <!-- Tags -->
  <div class="form-group m-t-10">
    <label class="control-label">Tags</label>
    <small class="text-muted d-block">Comma-separated</small>
    <input type="text" class="form-control" [(ngModel)]="tagsString" placeholder="tag1, tag2, tag3" />
  </div>

  <!-- Status -->
  @if (!isTemplateMode) {
    <div class="form-group m-t-10">
      <label class="control-label">Status</label>
      <select class="form-control" style="width: 180px" [(ngModel)]="status">
        <option value="draft">Draft</option>
        <option value="published">Published</option>
        <option value="archived">Archived</option>
      </select>
    </div>
  }
</div>
<div class="modal-footer">
  @if (dateError) {
    <div class="text-danger m-r-auto">{{ dateError }}</div>
  }
  <button
    type="button"
    class="btn btn-primary"
    [disabled]="
      saving ||
      dateError ||
      (showSharedFields && (!title || !slug || !description)) ||
      (!isTemplateMode && !startDatetime) ||
      (!isTemplateMode && !endDatetime) ||
      (isRecurring && recurringFrequency !== 'intermittent' && !recurringEndDate) ||
      (editingRecurrence && recurringFrequency !== 'intermittent' && !recurringEndDate) ||
      !livestreamFieldsValid
    "
    (click)="save()"
  >
    @if (saving) {
      <span>Saving...</span>
    } @else if (addOccurrenceMode) {
      <span>Add</span>
    } @else if (isNew) {
      <span>Create</span>
    } @else {
      <span>Update</span>
    }
  </button>
  <button type="button" class="btn btn-blank" (click)="activeModal.close()">Cancel</button>
</div>
